import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { AlertTriangle, Calendar, Check, History as HistoryIcon, LayoutDashboard, Loader2, Pencil, Trash2, X } from "lucide-react";
import { GroupOfPeopleIcon } from "../../../../shared/ui/icons/SubjectKindIcon";

import PageMeta from "../../../../shared/ui/common/PageMeta";
import ComponentCard from "../../../../shared/ui/common/ComponentCard";
import Button from "../../../../shared/ui/button/Button";
import Input from "../../../../shared/ui/form/input/InputField";
import { PageHeader } from "../../../../shared/ui/primitives";
import TabSwitcher, { type TabDefinition } from "../../../../shared/ui/tabs/TabSwitcher";
import { getAvatarColor } from "../../../../shared/utils/avatar";
import { useTherapist } from "../../../../context/TherapistProvider";
import { useCenter } from "../../../center";
import { GroupPatient, groupLabel, MIN_GROUP_SIZE, groupErrorMessage, useAddGroupMember, useDeleteGroupPatient, useGroupPatient, useRemoveGroupMember, useRenameGroupPatient, usePatientOptions, GroupMemberPicker, PickedMember, PatientOwnershipBadge } from "../../../patient";
import PatientNextSession from "../components/PatientNextSession";
import PatientToolDock from "../components/PatientToolDock";
import PatientViewSessions from "../components/PatientViewSessions";

type GroupTab = "overview" | "sessions";
const PANEL_ID = "group-patient-panel";

/** The name, inline-editable. Empty goes back to the members' first names. */
function GroupName({ group }: { group: GroupPatient }) {
    const { t } = useTranslation();
    const rename = useRenameGroupPatient(group.id);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const [error, setError] = useState<string | null>(null);

    const start = () => {
        setDraft(group.display_name ?? "");
        setError(null);
        setEditing(true);
    };
    const save = async () => {
        try {
            await rename.mutateAsync(draft.trim() || null);
            setEditing(false);
        } catch (err) {
            setError(groupErrorMessage(err, t));
        }
    };

    if (!editing) {
        return (
            <button
                type="button"
                onClick={start}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
            >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                {t("patient.group.rename")}
            </button>
        );
    }

    return (
        <div className="w-full space-y-2">
            <Input
                id="group-rename"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t("patient.group.namePlaceholder")}
                hint={t("patient.group.nameHint")}
            />
            {error && (
                <p role="alert" className="text-xs text-red-600 dark:text-red-400">
                    {error}
                </p>
            )}
            <div className="flex justify-center gap-2">
                <Button size="sm" onClick={save} loading={rename.isPending} startIcon={<Check className="h-3.5 w-3.5" />}>
                    {t("common.save")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} startIcon={<X className="h-3.5 w-3.5" />}>
                    {t("common.cancel")}
                </Button>
            </div>
        </div>
    );
}

/** Who is in the group, and who may still join: the therapist's, matching ownership. */
function GroupMembers({ group }: { group: GroupPatient }) {
    const { t } = useTranslation();
    const { therapist } = useTherapist();
    const { center } = useCenter();
    const { options, loading } = usePatientOptions(therapist?.id);
    const add = useAddGroupMember(group.id);
    const remove = useRemoveGroupMember(group.id);
    const [error, setError] = useState<string | null>(null);

    const inGroup = new Set(group.members.map((m) => m.id));
    const candidates = options.filter(
        (option) =>
            !inGroup.has(option.value) && (!center || option.heldByCenter === group.held_by_center)
    );
    const members: PickedMember[] = group.members.map((m) => ({
        id: m.id,
        name: `${m.first_name} ${m.last_name}`,
    }));

    const run = async (write: () => Promise<unknown>) => {
        setError(null);
        try {
            await write();
        } catch (err) {
            setError(groupErrorMessage(err, t));
        }
    };

    return (
        <ComponentCard glass={true} title={t("patient.group.members")}>
            <GroupMemberPicker
                members={members}
                candidates={candidates}
                loading={loading}
                disabled={add.isPending || remove.isPending}
                onAdd={(patientId) => run(() => add.mutateAsync(patientId))}
                onRemove={(member) => {
                    if (!confirm(t("patient.group.removeConfirm", { name: member.name }))) return;
                    run(() => remove.mutateAsync(member.id));
                }}
                renderName={(member) => (
                    <Link to={`/patient/${member.id}`} className="hover:text-brand-600 hover:underline">
                        {member.name}
                    </Link>
                )}
            />
            {error && (
                <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
                    {error}
                </p>
            )}
        </ComponentCard>
    );
}

export default function GroupPatientPage() {
    const { t, i18n } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { center } = useCenter();
    const { group, loading, failed } = useGroupPatient(id);
    const remove = useDeleteGroupPatient();
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab: GroupTab = searchParams.get("tab") === "sessions" ? "sessions" : "overview";
    const setActiveTab = (tab: GroupTab) => setSearchParams({ tab }, { replace: true });

    if (loading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Loader2 className="h-10 w-10 animate-spin text-brand-500 opacity-50" aria-label={t("common.loading")} />
            </div>
        );
    }
    if (failed || !group) {
        return <div className="p-8 text-center text-red-500">{t("patient.group.notFound")}</div>;
    }

    const name = groupLabel(group);
    const handleDelete = async () => {
        if (!confirm(t("patient.group.deleteConfirm"))) return;
        setDeleteError(null);
        try {
            await remove.mutateAsync(group.id);
        } catch {
            // Said, not swallowed: the group is still here, so stay on it.
            setDeleteError(t("patient.errorDeleting"));
            return;
        }
        navigate("/patients?tab=group");
    };

    const tabs: TabDefinition<GroupTab>[] = [
        { id: "overview", label: t("patient.tabs.overview"), icon: LayoutDashboard },
        { id: "sessions", label: t("patient.tabs.sessions"), icon: HistoryIcon },
    ];

    return (
        <div className="min-h-screen pb-20 md:pr-24">
            <PageMeta title={name} />
            <PatientToolDock groupPatientId={group.id} subjectLabel={name} />
            <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
                <PageHeader className="mb-6" eyebrow={t("patient.group.eyebrow")} title={name} />

                <div className="mb-8">
                    <TabSwitcher
                        tabs={tabs}
                        activeTab={activeTab}
                        onChange={setActiveTab}
                        label={name}
                        layoutId="activeTabGroupPatient"
                        panelId={PANEL_ID}
                    />
                </div>

                <div id={PANEL_ID} role="tabpanel" aria-label={tabs.find((tab) => tab.id === activeTab)?.label}>
                {activeTab === "sessions" ? (
                    <PatientViewSessions patientID={group.id} isGroup={true} />
                ) : (
                <div className="space-y-8">
                <PatientNextSession kind="group" id={group.id} label={name} />

                {group.below_minimum && (
                    <p
                        role="status"
                        className="mb-6 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
                    >
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                        {t("patient.group.belowMinimum", { min: MIN_GROUP_SIZE })}
                    </p>
                )}

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <ComponentCard glass={true} title="">
                        <div className="flex flex-col items-center gap-4 p-2 text-center">
                            <div className={`flex h-16 w-16 items-center justify-center rounded-full ${getAvatarColor(group.id)}`}>
                                <GroupOfPeopleIcon className="h-8 w-8" aria-hidden="true" />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-content-primary">{name}</h2>
                                <p className="mt-1 text-xs text-content-tertiary">
                                    {t("patient.group.membersCount", { count: group.members.length })}
                                </p>
                                {center && (
                                    <PatientOwnershipBadge heldByCenter={group.held_by_center} className="mt-2" />
                                )}
                                {group.created_at && (
                                    <p className="mt-1 flex items-center justify-center gap-1 text-xs text-content-tertiary">
                                        <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                                        {t("patient.group.since", {
                                            date: new Date(group.created_at).toLocaleDateString(i18n.language),
                                        })}
                                    </p>
                                )}
                            </div>
                            <GroupName group={group} />
                            <div className="mt-2 w-full border-t border-red-100 pt-4 dark:border-red-900/20">
                                <Button
                                    variant="outline"
                                    className="w-full border-red-200 text-red-500 hover:border-red-300 hover:bg-red-50 hover:text-red-600 dark:border-red-900/40 dark:hover:bg-red-950/30"
                                    onClick={handleDelete}
                                    loading={remove.isPending}
                                    startIcon={<Trash2 className="h-3.5 w-3.5" />}
                                    size="sm"
                                >
                                    {t("patient.group.delete")}
                                </Button>
                                {deleteError && (
                                    <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
                                        {deleteError}
                                    </p>
                                )}
                            </div>
                        </div>
                    </ComponentCard>

                    <div className="lg:col-span-2">
                        <GroupMembers group={group} />
                    </div>
                </div>

                <section>
                    <div className="mb-6 flex items-center justify-between">
                        <h3 className="flex items-center gap-3 text-xl font-bold text-content-primary">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-500 dark:bg-white/5">
                                <Calendar className="h-5 w-5" aria-hidden="true" />
                            </span>
                            {t("patient.recentSessions")}
                        </h3>
                        <button
                            type="button"
                            onClick={() => setActiveTab("sessions")}
                            className="text-sm font-semibold text-brand-600 hover:underline"
                        >
                            {t("common.showMore")}
                        </button>
                    </div>
                    <PatientViewSessions patientID={group.id} isGroup={true} pageSize={5} hideHeader={true} simple={true} />
                </section>
                </div>
                )}
                </div>
            </div>
        </div>
    );
}
